import { useState } from 'react';
import type { MpSession, UpdateMpSessionInput } from '@accounting/shared';
import { AttachmentsPanel } from '../attachments/AttachmentsPanel';
import { useParticipationSession, useUpdateParticipationSession } from '../../hooks/useParticipation';
import { ApiError } from '../../lib/api';

// Fix or adjust one session. Used for the exceptions at the top of the hours
// page and for any row of the log. Times are edited in the device's own time
// zone, which is how a datetime-local input works.

const EXCEPTION_TEXT: Record<string, string> = {
  leave_without_arrive: 'A leave came in with no arrive before it. Enter when you arrived.',
  arrive_while_open: 'You arrived again before any leave was received. Enter when you left.',
  overlap: 'This overlaps time recorded somewhere else. Correct the times, or mark one as not work.',
  clock_skew: 'The phone and the server disagreed about the time. Check the times and confirm.',
  missed_leave: 'No leave was received, or the visit ran past the maximum length. Enter when you left.',
};

export function exceptionText(session: MpSession): string {
  return EXCEPTION_TEXT[session.exceptionType ?? ''] ?? 'This session needs a look before it can count.';
}

/** ISO instant to the value a datetime-local input wants, in the device's zone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function formatInstant(iso: string | null): string {
  if (!iso) return 'not recorded';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function SessionEditor({
  session,
  locked,
  onDone,
}: {
  session: MpSession;
  /** True when the session's tax year is closed. Everything becomes read only. */
  locked: boolean;
  onDone: () => void;
}) {
  const update = useUpdateParticipationSession();
  const detail = useParticipationSession(session.id);
  const [start, setStart] = useState(toLocalInput(session.startAt));
  const [end, setEnd] = useState(toLocalInput(session.endAt));
  const [note, setNote] = useState(session.note ?? '');
  const [travel, setTravel] = useState(String(session.travelMinutes));
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const timesChanged = start !== toLocalInput(session.startAt) || end !== toLocalInput(session.endAt);

  const save = (status?: 'confirmed' | 'excluded_personal') => {
    setFormError(null);
    const input: UpdateMpSessionInput = {};
    if (timesChanged) {
      const startIso = fromLocalInput(start);
      const endIso = fromLocalInput(end);
      if (start && !startIso) return setFormError('The start time is not a valid date and time.');
      if (end && !endIso) return setFormError('The end time is not a valid date and time.');
      if (!reason.trim()) return setFormError('Give a short reason for changing the times.');
      if (startIso && startIso !== session.startAt) input.startAt = startIso;
      if (endIso && endIso !== session.endAt) input.endAt = endIso;
      input.reason = reason.trim();
    }
    if (note !== (session.note ?? '')) input.note = note;
    if (travel !== String(session.travelMinutes)) {
      const minutes = Number(travel || '0');
      if (!Number.isInteger(minutes) || minutes < 0 || minutes > 600) {
        return setFormError('Travel must be a whole number of minutes from 0 to 600.');
      }
      input.travelMinutes = minutes;
    }
    if (status && status !== session.status) input.status = status;
    if (Object.keys(input).length === 0) return onDone();
    update.mutate({ sessionId: session.id, input }, { onSuccess: onDone });
  };

  const apiError =
    update.error instanceof ApiError
      ? ((update.error.body as { error?: string } | null)?.error ?? 'Save failed')
      : update.error?.message;
  const needsAttention = session.status === 'exception' || session.status === 'open';

  return (
    <div className="mp-editor">
      <div className="mp-editor__head">
        <strong>{session.companyName}</strong>
        <span className="muted">{session.placeName ?? 'No saved location'}</span>
        {session.source === 'manual' && <span className="tag warn">manual</span>}
        {session.isEdited && <span className="tag">edited</span>}
      </div>
      {needsAttention && <p className="mp-editor__why">{exceptionText(session)}</p>}
      {locked && <p className="field-error">This tax year is closed. Reopen it to make changes.</p>}

      <div className="mp-editor__grid">
        <label htmlFor={`mp-start-${session.id}`}>Arrived</label>
        <input
          id={`mp-start-${session.id}`}
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          disabled={locked}
        />
        <label htmlFor={`mp-end-${session.id}`}>Left</label>
        <input
          id={`mp-end-${session.id}`}
          type="datetime-local"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          disabled={locked}
        />
        <label htmlFor={`mp-note-${session.id}`}>Note</label>
        <input
          id={`mp-note-${session.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          disabled={locked}
        />
        <label htmlFor={'mp-travel-' + session.id}>Travel (min)</label>
        <input
          id={'mp-travel-' + session.id}
          type="text"
          inputMode="numeric"
          value={travel}
          onChange={(e) => setTravel(e.target.value)}
          disabled={locked}
          style={{ maxWidth: 120 }}
        />
        {timesChanged && (
          <>
            <label htmlFor={`mp-reason-${session.id}`}>Reason</label>
            <input
              id={`mp-reason-${session.id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              placeholder="Why the times are being changed"
            />
          </>
        )}
      </div>

      <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
        Travel is an estimate, kept apart from the on-site time. Set it to 0 when you came from
        another business and made no separate trip, or add it when you made a second trip that day.
      </p>

      {(formError ?? apiError) && <div className="field-error">{formError ?? apiError}</div>}

      {!locked && (
        <div className="mp-editor__actions">
          <button className="btn-primary" onClick={() => save('confirmed')} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : needsAttention ? 'Save and count it' : 'Save'}
          </button>
          {session.status !== 'excluded_personal' && (
            <button className="btn-secondary" onClick={() => save('excluded_personal')} disabled={update.isPending}>
              Not work
            </button>
          )}
          <button className="btn-secondary" onClick={onDone} disabled={update.isPending}>
            Cancel
          </button>
        </div>
      )}
      {locked && (
        <button className="btn-secondary" onClick={onDone}>
          Close
        </button>
      )}

      {(detail.data?.edits.length ?? 0) > 0 && (
        <div className="mp-editor__history">
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
            Changes made by hand
          </div>
          {detail.data!.edits.map((e) => (
            <div key={e.id} style={{ fontSize: 12 }}>
              {formatInstant(e.editedAt)}: {e.field} changed
              {e.reason ? <span className="muted"> ({e.reason})</span> : null}
            </div>
          ))}
        </div>
      )}

      {/* The existing attachment system. A photo or receipt backs up the visit. */}
      <div style={{ marginTop: 12 }}>
        <AttachmentsPanel companyId={session.companyId} entityType="participation_session" entityId={session.id} />
      </div>
    </div>
  );
}
